/**
 * nts-hl-rules.js — v3
 * CartRulesEngine — middleware pipeline for cart add operations.
 * Pure sync. No API calls. No rendering. No events.
 *
 * Items use GQL format: { merchandiseId, quantity, attributes? }
 */
class CartRulesEngine {
  constructor() {
    this._rules = [];
  }

  use(rule) {
    this._rules.push(rule);
    this._rules.sort((a, b) => (a.priority || 50) - (b.priority || 50));
    return this;
  }

  /**
   * @param {Array} items    - [{merchandiseId, quantity, attributes?}]  (GQL CartLineInput format)
   * @param {string} trigger - 'bundle'|'single'|'upsell'
   * @param {Object} cart    - frozen cart snapshot from CartManager
   * @returns {Object} ctx   - { batch, skipped, injected, meta }
   */
  resolve(items, trigger, cart) {
    const ctx = {
      batch:    items.map(i => ({ ...i })),
      cart:     Object.freeze({ ...cart, items: [...(cart.items || [])] }),
      trigger,
      skipped:  [],
      injected: [],
      meta:     {},
    };

    for (const rule of this._rules) {
      if (!rule.enabled) continue;
      if (rule.condition && !rule.condition(ctx)) continue;
      rule.resolve(ctx);
    }

    return ctx;
  }
}

/* ============================================================
   BUILT-IN RULES — GQL format (merchandiseId instead of id)
   ============================================================ */

function _gid(variantId) {
  return 'gid://shopify/ProductVariant/' + variantId;
}

function _variantNum(gid) {
  return parseInt(String(gid).split('/').pop(), 10);
}

function createDedupeRule(config) {
  return {
    name:     'dedupe',
    enabled:  config.enabled !== false,
    priority: 10,
    condition: null,
    resolve(ctx) {
      if (config.mode === 'merge') return;
      const cartIds = new Set((ctx.cart.items || []).map(i => i.variantId));
      ctx.batch = ctx.batch.filter(item => {
        const numId = _variantNum(item.merchandiseId);
        if (cartIds.has(numId)) {
          ctx.skipped.push({ rule: 'dedupe', id: numId, reason: 'already in cart' });
          return false;
        }
        return true;
      });
    },
  };
}

function createShipInsureRule(config) {
  return {
    name:     'ship-insure',
    enabled:  config.enabled !== false,
    priority: 20,
    condition(ctx) {
      if (!config.enabled) return false;
      if ((ctx.cart.items || []).some(i => i.variantId === config.variantId)) return false;
      if (ctx.batch.some(i => _variantNum(i.merchandiseId) === config.variantId)) return false;
      if (config.condition === 'bundle-only' && ctx.trigger !== 'bundle') return false;
      return true;
    },
    resolve(ctx) {
      const line = { merchandiseId: _gid(config.variantId), quantity: 1 };
      if (config.attributes) line.attributes = config.attributes;
      ctx.batch.push(line);
      ctx.injected.push({ rule: 'ship-insure', id: config.variantId });
    },
  };
}

function createFreeGiftRule(config) {
  return {
    name:     'free-gift',
    enabled:  config.enabled !== false,
    priority: 30,
    condition(ctx) {
      if (!config.enabled) return false;
      if ((ctx.cart.items || []).some(i => i.variantId === config.variantId)) return false;
      if (ctx.batch.some(i => _variantNum(i.merchandiseId) === config.variantId)) return false;
      if (config.requiredVariants && config.requiredVariants.length > 0) {
        const allIds = new Set([
          ...(ctx.cart.items || []).map(i => i.variantId),
          ...ctx.batch.map(i => _variantNum(i.merchandiseId)),
        ]);
        return config.requiredVariants.every(v => allIds.has(v));
      }
      if (config.subtotalMin) {
        const cartTotal = (ctx.cart.items || []).reduce((s, i) => s + i.price * i.quantity, 0);
        return cartTotal >= config.subtotalMin;
      }
      return true;
    },
    resolve(ctx) {
      const line = { merchandiseId: _gid(config.variantId), quantity: 1 };
      if (config.attributes) line.attributes = config.attributes;
      ctx.batch.push(line);
      ctx.injected.push({ rule: 'free-gift', id: config.variantId });
    },
  };
}

function createQuantityCapRule(config) {
  return {
    name:     'quantity-cap',
    enabled:  config.enabled !== false,
    priority: 40,
    condition: null,
    resolve(ctx) {
      const cartQty = {};
      (ctx.cart.items || []).forEach(i => { cartQty[i.variantId] = (cartQty[i.variantId] || 0) + i.quantity; });

      ctx.batch = ctx.batch.filter(item => {
        const numId = _variantNum(item.merchandiseId);
        const max = (config.overrides && config.overrides[numId]) || config.defaultMax || 99;
        const inCart = cartQty[numId] || 0;
        if (inCart >= max) {
          ctx.skipped.push({ rule: 'quantity-cap', id: numId, reason: 'at max ' + max });
          return false;
        }
        item.quantity = Math.min(item.quantity || 1, max - inCart);
        return true;
      });
    },
  };
}

window.NTS = window.NTS || {};
